import { useCallback, useEffect, useRef } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useCallActions } from '#features/calls/hooks/useCallActions';
import { hangupCall } from '#features/calls/model/calls';
import { useCallStore } from '#features/calls/model/callStore';
import { envConfig } from '#constants/envConfig';

/** Уже отработанные ссылки: возврат «назад» на тот же адрес не должен отвечать на звонок повторно. */
const handledLinks = new Set<string>();

type WebCallAction = 'answer' | 'decline' | 'open';

const readAction = (value: string | null): WebCallAction =>
  value === 'answer' || value === 'decline' ? value : 'open';

/**
 * Ответ на звонок, пришедший веб-пушем.
 *
 * <p>Веб-двойник {@link useNativeCallBridge}: на телефоне уведомление открывает приложение
 * deep-link'ом, в браузере — обычным адресом с `?call=<id>&action=answer|decline`, который
 * ставит service worker. Дальше всё то же самое: своё представление о звонке у вкладки, которую
 * только что открыли, отсутствует — состояние приходится спрашивать у сервера.
 *
 * <p>Ждём авторизации по той же причине, что и мост: холодный старт отдаёт адрес раньше, чем
 * прочитана сессия, и ответ без неё просто не прошёл бы. Отказ, наоборот, отправляем сразу по
 * id: человек уже решил, и лишний круг к серверу тут только продлевает звонок у звонящего.
 *
 * <p>Параметры вычищаются из адреса сразу: перезагрузка страницы или возврат по истории иначе
 * ответили бы на звонок, которого давно нет.
 */
export const useWebCallLink = () => {
  const isAuth = useIsAuth();
  const { answer, resync } = useCallActions();
  const pendingRef = useRef<{ callId: string; action: WebCallAction } | null>(null);

  const runPending = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }
    pendingRef.current = null;

    if (pending.action === 'decline') {
      hangupCall(pending.callId).catch(() => undefined);
      const current = useCallStore.getState();
      if (current.call?.callId === pending.callId) {
        current.setEnded('declined');
      }
      return;
    }

    // Имя и аватар звонящего сюда не доезжают: в адресе их нет намеренно — ссылку из
    // уведомления видно в адресной строке, и подставлять оттуда что-либо в интерфейс нельзя.
    // Сверка с сервером всё равно вернёт настоящие.
    await resync({ callId: pending.callId, displayName: null, avatar: null });

    const store = useCallStore.getState();
    if (store.call?.callId !== pending.callId || store.phase !== 'incoming') {
      // Звонок уже отменён, принят на другом устройстве или истёк — оверлей (или его отсутствие)
      // и так показывает правду, а ответ на протухшую ссылку оборвал бы живой разговор.
      return;
    }

    if (pending.action === 'answer') {
      await answer();
    }
  }, [answer, resync]);

  useEffect(() => {
    if (envConfig.IS_NATIVE || typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const callId = params.get('call');
    if (!callId) {
      return;
    }

    const action = readAction(params.get('action'));
    const linkKey = `${callId}:${action}`;

    params.delete('call');
    params.delete('action');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    );

    if (handledLinks.has(linkKey)) {
      return;
    }
    handledLinks.add(linkKey);
    pendingRef.current = { callId, action };
  }, []);

  useEffect(() => {
    if (envConfig.IS_NATIVE || !isAuth) {
      return;
    }
    void runPending();
  }, [isAuth, runPending]);
};
